/**
 * Czysta matematyka follow-kamery z deadzone + lerp — bez Phaser.
 * Viewport = GAME_WIDTH × GAME_HEIGHT; świat może być szerszy.
 */
import type { DeadzoneSize } from './types.js';
import { DEFAULT_DEADZONE, DEFAULT_FOLLOW_LERP } from './types.js';

export type FollowCameraState = {
  scrollX: number;
  scrollY: number;
};

export type FollowCameraOpts = {
  lerp?: number;
  deadzone?: DeadzoneSize;
  viewW: number;
  viewH: number;
  worldW: number;
  worldH: number;
};

/**
 * Aktualizuje scroll kamery tak, by cel zostawał w deadzone względem środka viewportu.
 * Poza deadzone: lerp w stronę pozycji, która centruje cel (z clampem do bounds świata).
 */
export function stepFollowCamera(
  state: FollowCameraState,
  targetX: number,
  targetY: number,
  opts: FollowCameraOpts,
): FollowCameraState {
  const lerp = opts.lerp ?? DEFAULT_FOLLOW_LERP;
  const dz = opts.deadzone ?? DEFAULT_DEADZONE;
  const { viewW, viewH, worldW, worldH } = opts;

  // Pozycja celu w przestrzeni ekranu (względem scroll)
  const screenX = targetX - state.scrollX;
  const screenY = targetY - state.scrollY;
  const midX = viewW / 2;
  const midY = viewH / 2;
  const halfDzW = dz.width / 2;
  const halfDzH = dz.height / 2;

  let desiredScrollX = state.scrollX;
  let desiredScrollY = state.scrollY;

  if (screenX < midX - halfDzW) {
    desiredScrollX = targetX - (midX - halfDzW);
  } else if (screenX > midX + halfDzW) {
    desiredScrollX = targetX - (midX + halfDzW);
  }

  if (screenY < midY - halfDzH) {
    desiredScrollY = targetY - (midY - halfDzH);
  } else if (screenY > midY + halfDzH) {
    desiredScrollY = targetY - (midY + halfDzH);
  }

  const maxScrollX = Math.max(0, worldW - viewW);
  const maxScrollY = Math.max(0, worldH - viewH);
  desiredScrollX = Math.min(maxScrollX, Math.max(0, desiredScrollX));
  desiredScrollY = Math.min(maxScrollY, Math.max(0, desiredScrollY));

  return {
    scrollX: state.scrollX + (desiredScrollX - state.scrollX) * lerp,
    scrollY: state.scrollY + (desiredScrollY - state.scrollY) * lerp,
  };
}
